import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  CartItemState,
  Position,
} from '@sorare/core/src/__generated__/globalTypes';
import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineS, LabelS } from '@sorare/core/src/atoms/typography';
import { HorizontalDivider } from '@sorare/core/src/atoms/ui/Divider';
import { PositionIcon } from '@sorare/core/src/components/lineup/PositionIcon';
import { footballPositions } from '@sorare/core/src/lib/players';

import { useCart } from 'hooks/useCart';

import { CartItem } from '../CartItem';
import { EmptyCart } from '../EmptyCart';
import { MainCartItems_cartItem } from './__generated__/index.graphql';

const Header = styled(Horizontal).attrs({ gap: 0.5 })``;

const Section = styled(Vertical).attrs({ gap: 2 })`
  width: 100%;
`;
const Wrapper = styled(Vertical).attrs({ gap: 1 })`
  width: 100%;
`;
const CartItemWrapper = styled(Vertical)`
  width: 100%;
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  background-color: var(--c-nd-50);
`;

const CartItemSkeleton = styled(Skeleton)`
  width: 100%;
  height: 160px;
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  background-color: var(--c-nd-50);
`;

const PositionCount = styled(Horizontal).attrs({ gap: 0.5 })`
  padding: var(--half-unit) var(--unit);
  border-radius: var(--half-unit);
  background-color: var(--c-nd-100);
`;

type CartItemsProps = {
  cartItems: MainCartItems_cartItem[];
  count: number;
};
const CartItems = ({ cartItems, count }: CartItemsProps) => {
  return (
    <Wrapper>
      {cartItems.map(cartItem => (
        <CartItemWrapper key={cartItem.id}>
          <CartItem cartItem={cartItem} card={cartItem.card} />
        </CartItemWrapper>
      ))}
      {count - cartItems.length > 0 &&
        Array.from({ length: count - cartItems.length }, (v, i) => i).map(i => (
          <CartItemSkeleton key={i} />
        ))}
    </Wrapper>
  );
};

type Props = {
  cartItems: MainCartItems_cartItem[];
};

export const MainCartItems = ({ cartItems }: Props) => {
  const { cartCount, removedCartCount } = useCart();

  const availableCartItems = cartItems.filter(
    cartItem => cartItem.state !== CartItemState.REMOVED
  );

  const removedCartItems = cartItems.filter(
    cartItem => cartItem.state === CartItemState.REMOVED
  );

  const facetCardsByPosition = cartItems.reduce(
    (acc, { card }) => {
      const position = card.anyPositions[0];
      acc[position] = !acc[position] ? 1 : acc[position] + 1;
      return acc;
    },
    {} as Record<Position, number>
  );
  if (!cartCount && !removedCartCount) return <EmptyCart />;
  return (
    <Vertical>
      <Header>
        <HeadlineS as="h2">
          <FormattedMessage
            id="cartItems.title"
            defaultMessage="{total} {total, plural, one {card} other {Cards}}"
            values={{ total: cartItems.length || cartCount }}
          />
        </HeadlineS>
        {footballPositions.map(
          position =>
            facetCardsByPosition?.[position] && (
              <PositionCount center key={position}>
                <PositionIcon position={position as Position} />
                <LabelS bold>{facetCardsByPosition?.[position]}</LabelS>
              </PositionCount>
            )
        )}
      </Header>
      <Section>
        <CartItems cartItems={availableCartItems} count={cartCount} />
        {!!cartCount && !!removedCartCount && <HorizontalDivider />}
        <CartItems cartItems={removedCartItems} count={removedCartCount} />
      </Section>
    </Vertical>
  );
};

MainCartItems.fragments = {
  cartItem: gql`
    fragment MainCartItems_cartItem on CartItem {
      id
      card {
        slug
        anyPositions
        ...CartItem_anyCard
      }
      ...CartItem_cartItem
    }
    ${CartItem.fragments.anyCard}
    ${CartItem.fragments.cartItem}
  ` as TypedDocumentNode<MainCartItems_cartItem>,
};
